/**
 * QuoteDashboardCards ✦ iter227
 *
 * Two side-by-side blocks shown at the top of /admin/quotes:
 *   • Conversion Metrics (Created/Sent/Accepted/Declined/Expired
 *     + conversion %) with Month/Year/All-Time toggle.
 *   • Revenue Forecast (Outstanding + Accepted + Confirmed
 *     bookings = Expected revenue).
 *
 * The point of this block isn't reporting — it's making the
 * answer to "am I selling more than last month" a glanceable
 * fact instead of a spreadsheet exercise.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#D4AF5E';
const GOLD_FAINT = 'rgba(212,170,82,0.22)';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.62)';
const IVORY_GHOST = 'rgba(251,238,208,0.42)';

const WINDOWS = [
  { key: 'month',    label: 'This month' },
  { key: 'year',     label: 'This year' },
  { key: 'all_time', label: 'All time' },
];

const fmtCurrency = (n) => `£${(Number(n) || 0).toLocaleString('en-GB')}`;

const QuoteDashboardCards = ({ refreshKey }) => {
  const [windowMode, setWindowMode] = useState('month');
  const [metrics, setMetrics] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, f] = await Promise.all([
        axios.get(`${API}/admin/quote-pipeline/conversion-metrics`, { params: { window: windowMode } }),
        axios.get(`${API}/admin/quote-pipeline/revenue-forecast`),
      ]);
      setMetrics(m.data);
      setForecast(f.data);
    } catch {
      setMetrics(null);
      setForecast(null);
    } finally {
      setLoading(false);
    }
  }, [windowMode]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <section data-testid="quote-dashboard-cards"
      style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 16, marginBottom: 24,
      }}>
      {/* Conversion metrics */}
      <div style={cardStyle} data-testid="conversion-metrics-card">
        <div style={cardHeaderStyle}>
          <div>
            <div style={eyebrow}>✦ Conversion</div>
            <div style={cardTitle}>{metrics?.label || '—'}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {WINDOWS.map((w) => (
              <button type="button" key={w.key}
                onClick={() => setWindowMode(w.key)}
                data-testid={`metrics-window-${w.key}`}
                style={togglePill(windowMode === w.key)}>{w.label}</button>
            ))}
          </div>
        </div>
        {loading ? (
          <p style={loadingStyle}>Crunching…</p>
        ) : !metrics ? (
          <p style={loadingStyle}>No data.</p>
        ) : (
          <>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14,
            }}>
              <MetricCell label="Created"  value={metrics.counts.created}  testid="metric-created" />
              <MetricCell label="Sent"     value={metrics.counts.sent}     testid="metric-sent" />
              <MetricCell label="Accepted" value={metrics.counts.accepted} testid="metric-accepted" emphasis />
              <MetricCell label="Declined" value={metrics.counts.declined} testid="metric-declined" muted />
              <MetricCell label="Expired"  value={metrics.counts.expired}  testid="metric-expired" muted />
              <MetricCell label="Draft"    value={metrics.counts.draft}    testid="metric-draft" muted />
            </div>
            <div style={{
              borderTop: `1px solid ${GOLD_FAINT}`,
              paddingTop: 12,
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              flexWrap: 'wrap', gap: 8,
            }}>
              <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5, color: IVORY_DIM,
                letterSpacing: '0.04em' }}>
                Accepted ÷ Decided
              </span>
              <span data-testid="conversion-rate-decided"
                style={{ fontFamily: SERIF, fontSize: 26, color: GOLD, fontWeight: 500 }}>
                {metrics.conversion_rate_decided}%
              </span>
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              marginTop: 6,
            }}>
              <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11.5, color: IVORY_GHOST,
                letterSpacing: '0.04em' }}>
                Raw rate (accepted ÷ created)
              </span>
              <span data-testid="conversion-rate-raw"
                style={{ fontFamily: SERIF, fontSize: 14, color: IVORY_DIM }}>
                {metrics.conversion_rate_raw}%
              </span>
            </div>
          </>
        )}
      </div>

      {/* Revenue forecast */}
      <div style={cardStyle} data-testid="revenue-forecast-card">
        <div style={cardHeaderStyle}>
          <div>
            <div style={eyebrow}>✦ Revenue Forecast</div>
            <div style={cardTitle}>What&apos;s in the pipeline</div>
          </div>
        </div>
        {loading || !forecast ? (
          <p style={loadingStyle}>Crunching…</p>
        ) : (
          <>
            <ForecastRow label="Outstanding quotes" value={forecast.outstanding_quote_value}
              hint="Draft + Sent — not committed yet"
              testid="forecast-outstanding" />
            <ForecastRow label="Accepted quotes" value={forecast.accepted_quote_value}
              hint="Customer said yes — booking pending"
              testid="forecast-accepted" />
            <ForecastRow label="Confirmed bookings" value={forecast.confirmed_booking_value}
              hint="Deposit received · inventory deducted"
              testid="forecast-confirmed" />
            <div style={{
              marginTop: 8, paddingTop: 12,
              borderTop: `1px solid ${GOLD_FAINT}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            }}>
              <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5,
                color: IVORY_DIM, letterSpacing: '0.04em' }}>
                Expected revenue
              </span>
              <span data-testid="forecast-expected"
                style={{ fontFamily: SERIF, fontSize: 26, color: GOLD, fontWeight: 500 }}>
                {fmtCurrency(forecast.expected_revenue)}
              </span>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

const togglePill = (active) => ({
  padding: '5px 12px', borderRadius: 999,
  border: `1px solid ${active ? GOLD : 'rgba(255,255,255,0.14)'}`,
  background: active ? 'rgba(212,170,82,0.18)' : 'rgba(255,255,255,0.04)',
  color: active ? GOLD : IVORY_DIM,
  fontFamily: SERIF, fontStyle: 'italic', fontSize: 11.5, cursor: 'pointer',
});

const cardStyle = {
  padding: '20px 22px', borderRadius: 14,
  border: `1px solid ${GOLD_FAINT}`,
  background: 'rgba(255,255,255,0.03)',
  color: IVORY,
};
const cardHeaderStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
  marginBottom: 16, gap: 12, flexWrap: 'wrap',
};
const eyebrow = {
  fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 10.5, letterSpacing: '0.28em', textTransform: 'uppercase',
  color: GOLD, marginBottom: 6,
};
const cardTitle = {
  fontFamily: SERIF, fontWeight: 500, fontSize: 19, color: IVORY, letterSpacing: '-0.005em',
};
const loadingStyle = {
  fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM, textAlign: 'center', padding: '20px 0',
};

const MetricCell = ({ label, value, testid, emphasis, muted }) => (
  <div style={{
    padding: '10px 12px', borderRadius: 10,
    background: emphasis ? 'rgba(212,170,82,0.10)' : 'rgba(0,0,0,0.18)',
    border: `1px solid ${emphasis ? GOLD_FAINT : 'rgba(255,255,255,0.06)'}`,
  }}>
    <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 10,
      letterSpacing: '0.16em', textTransform: 'uppercase', color: IVORY_GHOST }}>
      {label}
    </div>
    <div data-testid={testid}
      style={{ fontFamily: SERIF, fontSize: 22,
        color: emphasis ? GOLD : (muted ? IVORY_DIM : IVORY),
        fontWeight: 500, marginTop: 2 }}>
      {value ?? 0}
    </div>
  </div>
);

const ForecastRow = ({ label, value, hint, testid }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    padding: '10px 0',
    borderTop: '1px solid rgba(255,255,255,0.04)',
  }}>
    <div>
      <div style={{ fontFamily: SERIF, fontSize: 14, color: IVORY }}>{label}</div>
      <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11, color: IVORY_GHOST,
        letterSpacing: '0.02em', marginTop: 2 }}>{hint}</div>
    </div>
    <span data-testid={testid}
      style={{ fontFamily: SERIF, fontSize: 17, color: IVORY, fontWeight: 500 }}>
      {fmtCurrency(value)}
    </span>
  </div>
);

export default QuoteDashboardCards;
